/**
 * 맵 묶음 → three 머티리얼.
 *
 * ORM 한 장을 aoMap/roughnessMap/metalnessMap 세 슬롯에 **같이** 물린다.
 * three는 각 슬롯에서 R/G/B 채널만 읽으므로 텍스처 업로드가 세 번이 아니라
 * 한 번이면 된다. glTF도 같은 규약이라 내보낼 때 변환이 없다.
 *
 * ## 머티리얼은 앱 수명 동안 하나다
 *
 * 이미지를 바꿀 때마다 MeshPhysicalMaterial을 새로 만들면 three가 셰이더를
 * 다시 컴파일한다 — 옛 머티리얼을 dispose 하는 순간 프로그램 캐시의 참조 수가
 * 0이 되어 지워지기 때문이다. Windows Chrome(ANGLE → D3D11 HLSL)에서 이
 * 컴파일이 **한 번에 17초** 걸리는 걸 실측했다. 갤러리를 누를 때마다 17초 멈추는
 * 앱은 앱이 아니다.
 *
 * 그래서 머티리얼은 한 번 만들고 텍스처만 갈아 끼운다. 셰이더 define을 바꾸는
 * 속성(alphaTest 유무, clearcoat 0↔양수, 맵 슬롯 유무, side)은 전부 상수로 못 박는다.
 * 첫 컴파일은 앱이 뜰 때 1×1 더미 텍스처로 미리 치른다(`buildPlaceholderMaterial`).
 */

import * as THREE from 'three';
import type { ForgeParams, MaterialMaps, Raster } from '../types.ts';
import { rasterToCanvas } from '../imaging/raster.ts';
import { attachStyle, createStyleUniforms, PROJ_MAX_VIEWS, type StyleUniforms } from '../../shaders/stylize.ts';
import type { ProjectionUniforms } from '../turnaround/hull.ts';

export interface ForgedMaterial {
  material: THREE.MeshPhysicalMaterial;
  uniforms: StyleUniforms;
  textures: THREE.Texture[];
  dispose: () => void;
}

function configureTexture(tex: THREE.Texture, srgb: boolean, anisotropy: number): void {
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = anisotropy;
  tex.needsUpdate = true;
}

function makeTexture(raster: Raster, srgb: boolean, anisotropy: number): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(rasterToCanvas(raster));
  configureTexture(tex, srgb, anisotropy);
  return tex;
}

/** 1×1 텍스처. 셰이더 define을 실전과 똑같이 만들기 위한 자리 채우기. */
function makeDummyTexture(rgba: [number, number, number, number], srgb: boolean): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array(rgba), 1, 1, THREE.RGBAFormat);
  // 1×1 에는 밉맵이 의미 없고, flipY 는 CanvasTexture 와 맞춰 둔다.
  tex.flipY = true;
  configureTexture(tex, srgb, 1);
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

/** 마스크를 albedo의 알파 채널에 굽는다. 슬롯 하나를 아끼고 alphaTest로 끝난다. */
function bakeAlpha(albedo: Raster, alpha: Float32Array): Raster {
  const out: Raster = {
    width: albedo.width,
    height: albedo.height,
    data: new Uint8ClampedArray(albedo.data),
  };
  for (let i = 0, p = 3; i < alpha.length; i++, p += 4) {
    out.data[p] = alpha[i]! * 255;
  }
  return out;
}

function createTextures(maps: MaterialMaps, anisotropy: number, cutout: boolean): THREE.Texture[] {
  // 단일 뷰는 마스크를 알파로 구워 실루엣 밖을 잘라낸다. 시트 모드는 굽지 않는다 —
  // 투영 UV 가 실루엣 밖을 스치는 면이 있는데 그걸 자르면 속이 빈 것처럼 뚫린다.
  const albedo = cutout ? bakeAlpha(maps.albedo, maps.alpha.data) : maps.albedo;
  return [
    makeTexture(albedo, true, anisotropy),
    makeTexture(maps.normal, false, anisotropy),
    makeTexture(maps.orm, false, anisotropy),
    makeTexture(maps.emissive, true, anisotropy),
  ];
}

function assignTextures(material: THREE.MeshPhysicalMaterial, t: THREE.Texture[]): void {
  const [map, normalMap, orm, emissiveMap] = t as [THREE.Texture, THREE.Texture, THREE.Texture, THREE.Texture];
  material.map = map;
  material.normalMap = normalMap;
  material.aoMap = orm;
  material.roughnessMap = orm;
  material.metalnessMap = orm;
  material.emissiveMap = emissiveMap;
}

function createBaseMaterial(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    normalScale: new THREE.Vector2(1, 1),
    emissive: new THREE.Color(0xffffff),
    emissiveIntensity: 0,
    roughness: 1,
    metalness: 1,
    aoMapIntensity: 1,
    side: THREE.DoubleSide,
    transparent: false,
    // 상수. 0 과 양수를 오가면 USE_ALPHATEST 가 바뀌어 재컴파일된다.
    alphaTest: 0.5,
    // three 의 clearcoat 는 쓰지 않는다 — 두 번째 BRDF 가 셰이더를 두 배로 불린다.
    // 젖은 느낌은 stylize.ts 의 프레넬 림으로 낸다.
    clearcoat: 0,
    envMapIntensity: 1,
  });
}

/**
 * 앱이 뜰 때 만드는 머티리얼. 1×1 텍스처를 물려 실전과 같은 define 으로 컴파일시킨다.
 * 이걸 보이지 않는 작은 메시에 물려 첫 프레임에 렌더하면, 사용자가 이미지를 고르는
 * 동안 컴파일이 끝나 있다.
 */
export function buildPlaceholderMaterial(): ForgedMaterial {
  const material = createBaseMaterial();
  const textures: THREE.Texture[] = [
    makeDummyTexture([128, 128, 128, 255], true),
    makeDummyTexture([128, 128, 255, 255], false),
    makeDummyTexture([255, 200, 0, 255], false),
    makeDummyTexture([0, 0, 0, 255], true),
  ];
  assignTextures(material, textures);
  const uniforms = createStyleUniforms();
  attachStyle(material, uniforms);
  return {
    material,
    uniforms,
    textures,
    dispose: () => {
      for (const t of textures) t.dispose();
      material.dispose();
    },
  };
}

/** 이미 있는 머티리얼에 새 맵을 갈아 끼운다. 셰이더는 그대로다. */
export function updateMaterialMaps(
  forged: ForgedMaterial,
  maps: MaterialMaps,
  params: ForgeParams,
  opts: { anisotropy?: number; cutout?: boolean; projection?: ProjectionUniforms | null } = {},
): void {
  const next = createTextures(maps, opts.anisotropy ?? 8, opts.cutout ?? true);
  applyProjection(forged.uniforms, opts.projection ?? null);
  const prev = forged.textures;
  assignTextures(forged.material, next);
  forged.textures = next;
  for (const t of prev) t.dispose();
  applyStyleParams(forged.uniforms, forged.material, params);
}

/** 처음부터 실제 맵으로 만든다. 테스트와 익스포터처럼 자리 채우기가 필요 없는 곳에서. */
export function buildMaterial(
  maps: MaterialMaps,
  params: ForgeParams,
  opts: { anisotropy?: number } = {},
): ForgedMaterial {
  const forged = buildPlaceholderMaterial();
  updateMaterialMaps(forged, maps, params, opts);
  return forged;
}

/** 재컴파일 없이 값만 갱신되는 것들. 슬라이더가 즉각 반응하는 이유. */
export function applyStyleParams(
  uniforms: StyleUniforms,
  material: THREE.MeshPhysicalMaterial,
  params: ForgeParams,
): void {
  uniforms.uToonSteps.value = params.toonSteps;
  uniforms.uToonMix.value = params.toonMix;
  uniforms.uRimColor.value.set(params.rimColor);
  uniforms.uRimStrength.value = params.rimStrength;
  uniforms.uIridStrength.value = params.iridescence;
  uniforms.uBackTint.value = params.meshMode === 'inflate' ? params.backTint : 0;

  material.normalScale.set(1, 1);
  material.emissiveIntensity = params.emissiveStrength > 0 ? 1 : 0;
  material.aoMapIntensity = 1;
}

/** N뷰 투영 유니폼을 채운다. null 이면 보통 uv 샘플링. */
export function applyProjection(uniforms: StyleUniforms, projection: ProjectionUniforms | null): void {
  if (!projection) {
    uniforms.uTriplanar.value = 0;
    uniforms.uProjCount.value = 0;
    return;
  }
  uniforms.uTriplanar.value = 1;
  uniforms.uProjCount.value = projection.count;
  const tex = uniforms.uProjTex.value;
  const data = tex.image.data as Float32Array;
  data.fill(0);
  data.set(projection.r.subarray(0, PROJ_MAX_VIEWS * 4), 0);
  data.set(projection.rect.subarray(0, PROJ_MAX_VIEWS * 4), PROJ_MAX_VIEWS * 4);
  tex.needsUpdate = true;
  uniforms.uProjOffset.value.copy(projection.offset);
  uniforms.uProjScale.value = projection.scale;
}
